import {
  InfiniteData,
  QueryKey,
  QueryObserverResult,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { HttpStatusCode, PejHttpError, ResultListModel } from '@common-lib';

import { useRoot } from '@frontend/utils';

export interface InfiniteScrollState<T> {
  loading: boolean;
  currentData: ResultListModel<T> | undefined;
  loadNext(): Promise<void>;
  hasNext: boolean;
}

interface InfiniteScrollClientReducerState<T> {
  loading: boolean;
  data: ResultListModel<T> | undefined;
  error: Error | undefined;
}

interface UseInfiniteScrollClientProps<T> {
  queryKey: QueryKey;
  enabled?: boolean;
  onFetch: (pageToken: string | undefined) => Promise<ResultListModel<T>>;
  onFinally?: () => void;
  onError?: (error: Error) => void;
}

interface UseInfiniteScrollClientResponse<T> {
  /**
   * The infinite scroll state should be passed to the scrollable component. It
   * contains the data needed for the component to work properly.
   */
  infiniteScrollState: InfiniteScrollState<T>;

  /**
   * This is the current state of the client if fine-grained
   * control is required.
   */
  state: InfiniteScrollClientReducerState<T>;

  /**
   * Fetches the next set of data.
   *
   * @param pageToken the optional token, if undefined this will trigger
   *  a reset.
   */
  loadNext(pageToken: string | undefined): void;

  /**
   * Resets the paging and starts from the beginning again. This will
   * reset all data currently held by the client. If no data has been
   * retrieved yet and autoFetch is false this will trigger the
   * initial data fetch.
   *
   * Typical use-cases: triggering a search or filter on the dataset.
   */
  refetch(): Promise<QueryObserverResult<InfiniteData<ResultListModel<T>>>>;

  /**
   * Resets the paging and starts from the beginning again. This will
   * reset all data currently held by the client. Compared to refetch
   * this will not automatically trigger data fetching.
   */
  reset(): void;
}

export const useInfiniteScrollClientQuery = <T>({
  queryKey,
  enabled = true,
  onFetch,
  onFinally,
  onError,
}: UseInfiniteScrollClientProps<T>): UseInfiniteScrollClientResponse<T> => {
  const queryClient = useQueryClient();
  const { removeLocalSession } = useRoot();

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
    refetch,
    isSuccess,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: undefined,
    getNextPageParam: (lastPage: ResultListModel<T>) => {
      return lastPage.nextPageToken;
    },
    queryKey,
    enabled,
    queryFn: async ({ pageParam: pageToken }) => await onFetch(pageToken),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts once per error. onError and removeLocalSession are callbacks that can change identity every render, which would repeat them for the same error.
  useEffect(() => {
    if (isError) {
      if (error instanceof PejHttpError) {
        if (
          error.statusCode === HttpStatusCode.UNAUTHORIZED ||
          error.statusCode === HttpStatusCode.LOCKED
        ) {
          removeLocalSession();
        }
      }

      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }, [isError, error]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts once per finished fetch. onFinally is a callback that can change identity every render, which would repeat it.
  useEffect(() => {
    if (isSuccess && data && !isFetching) {
      onFinally?.();
    }
  }, [isSuccess, data, isFetching]);

  const reset = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const hasNext = (): boolean => {
    return hasNextPage;
  };

  return {
    infiniteScrollState: {
      loading: isFetching,
      currentData: data
        ? ({
            nextPageToken: data.pages[data.pages.length - 1]?.nextPageToken,
            results: data.pages.flatMap((page) => page.results),
            totalCount: data.pages[0]?.totalCount ?? 0,
          } satisfies ResultListModel<T>)
        : undefined,
      loadNext: async () => {
        fetchNextPage();
      },
      hasNext: hasNext(),
    },
    state: {
      loading: isFetching,
      data: data
        ? ({
            nextPageToken: data.pages[data.pages.length - 1]?.nextPageToken,
            results: data.pages.flatMap((page) => page.results),
            totalCount: data.pages[0]?.totalCount ?? 0,
          } satisfies ResultListModel<T>)
        : undefined,
      error: isError ? error : undefined,
    },
    refetch,
    reset,
    loadNext: async () => {
      fetchNextPage();
    },
  };
};
